import { useEffect } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { Amplify } from "aws-amplify";
import {
  Container,
  createTheme,
  CssBaseline,
  ThemeProvider,
} from "@mui/material";

import { AlertProvider } from "./components/AlertProvider";
import BottomNavBar from "./components/BottomNavBar";
import { DataProvider } from "./components/DataProvider";
import { ChurchesProvider } from "./contexts/ChurchesProvider";

import AccountTab from "./navigation/Tabs/AccountTab";
import AdminTab from "./navigation/Tabs/AdminTab";
import AttendeesTab from "./navigation/Tabs/AttendeesTab";
import ChurchesTab from "./navigation/Tabs/ChurchesTab";
import ChurchLayout from "./navigation/Tabs/ChurchLayout";
import ResourcesTab from "./navigation/Tabs/ResourcesTab";

import AccountPage from "./pages/Account";
import LoginPage from "./pages/Account/Login";
import AdminPage from "./pages/Admin";
import BaptizeAmericaOverview from "./pages/Admin/BaptizeAmericaOverview";
import CreateAdminPage from "./pages/Admin/CreateAdmin";
import Dashboard from "./pages/Admin/Dashboard";
import DisplayUsers from "./pages/Admin/DisplayUsers";
import InvitationsManager from "./pages/Admin/InvitationsManager";
import InviteChurchPage from "./pages/Admin/InviteChurch";
import AttendeesPage from "./pages/Attendees";
import CertificateGenerator from "./pages/BaptismCertificate";
import CheckInboxPage from "./pages/Churches/CheckInbox";
import CreateChurchPage from "./pages/Churches/CreateChurch";
import EditChurchPage from "./pages/Churches/EditChurch";
import CreateBaptismEventPage from "./pages/Churches/CreateBaptismEvent";
import ManageBaptismEventPage from "./pages/Churches/ManageBaptismEvent";
import ViewAllChurchesPage from "./pages/Churches/ViewAllChurches";
import ViewChurchDetailsPage from "./pages/Churches/ViewChurchDetails";
import ResourcesPage from "./pages/Resources";
import { CrispService } from "./services/CrispService";
import { configMap } from "./utils/helper";

import "./App.css";
import DeleteUser from "./pages/Admin/DeleteUser";

Amplify.configure({ Auth: configMap[window.location.hostname].auth });

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& legend": {
            transition: "unset",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: "#00169E",
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 300,
      md: 660,
      lg: 980,
      xl: 1620,
    },
  },
  typography: {
    fontFamily: ['"Instrument Sans"'].join(","), // Replace with your desired font family
    body1: {
      fontWeight: 400,
    },
  },
});

const TabNavigation = () => {
  return (
    <nav>
      <Link to="/admin" />
      <Link to="/dev" />
      <Link to="/churches" />
      <Link to="/account" />
      <Link to="/attendees" />
    </nav>
  );
};
const NoMatch = () => {
  return (
    <p>
      There's nothing here: 404! <a href={"/"}>Go Home</a>
    </p>
  );
};

function AppContent() {
  const location = useLocation();

  return (
    <AlertProvider>
      <ChurchesProvider>
        <DataProvider>
          <TabNavigation />
          <CssBaseline />
          <Container style={{ padding: 0 }}>
            <div style={{ paddingBottom: "5rem" }}>
              <Routes>
                <Route path="/" element={<Navigate to="/churches" />} />
                <Route path="/admin" element={<AdminTab />}>
                  <Route index element={<AdminPage />} />
                  <Route path="home" element={<AdminPage />} />
                  <Route path="create_admin" element={<CreateAdminPage />} />
                  <Route path="delete_user" element={<DeleteUser />} />
                  <Route path="invite_church" element={<InviteChurchPage />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="overview" element={<BaptizeAmericaOverview />} />
                  <Route path="users" element={<DisplayUsers />} />
                  <Route path="invitations" element={<InvitationsManager />} />
                </Route>
                {/*<Route path="/dev" element={<DevTab />}>*/}
                {/*  <Route index element={<DevPage />} />*/}
                {/*</Route>*/}
                <Route path="/churches" element={<ChurchesTab />}>
                  <Route index element={<ViewAllChurchesPage />} />
                  <Route path="view" element={<ViewAllChurchesPage />} />
                  <Route path="create" element={<CreateChurchPage />} />
                  <Route path="check_inbox" element={<CheckInboxPage />} />
                  <Route path=":churchId" element={<ChurchLayout />}>
                    <Route index element={<ViewChurchDetailsPage />} />
                    <Route path="edit" element={<EditChurchPage />} />
                    <Route
                      path="baptism/new"
                      element={<CreateBaptismEventPage />}
                    />
                    <Route
                      path="baptism/:baptismId/manage"
                      element={<ManageBaptismEventPage />}
                    />
                  </Route>
                </Route>
                <Route path="/attendees" element={<AttendeesTab />}>
                  <Route index element={<AttendeesPage />} />
                  <Route path=":churchId" element={<AttendeesPage />} />
                </Route>
                <Route path="/resources" element={<ResourcesTab />}>
                  <Route index element={<ResourcesPage />} />
                </Route>
                <Route path="/account" element={<AccountTab />}>
                  <Route index element={<AccountPage />} />
                  <Route path="login" element={<LoginPage />} />
                </Route>
                <Route
                  path="/certificate"
                  index
                  element={<CertificateGenerator />}
                />
                <Route path="*" element={<NoMatch />} />
              </Routes>
            </div>
            {location.pathname !== "/certificate" && <BottomNavBar />}
          </Container>
        </DataProvider>
      </ChurchesProvider>
    </AlertProvider>
  );
}

function App() {
  useEffect(() => {
    CrispService.init();
  }, []);

  return (
    <div>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
